import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../query/queryKeys';
import {
  listThreadMutesRequest,
  storeThreadMuteRequest,
  deleteThreadMuteRequest,
} from '../api/chatThreadMutesApi';
import {
  listBlocksRequest,
  storeBlockRequest,
  deleteBlockRequest,
} from '../api/chatBlocksApi';
import { getThreadViewRequest } from '../api/chatThreadViewApi';
import {
  listPinnedRequest,
  pinMessageRequest,
  unpinMessageRequest,
} from '../api/chatPinsApi';

/**
 * Phase B — TanStack hook for the per-thread mute list. Exposes a Set for
 * O(1) `mutedSet.has(threadId)` membership checks and a `toggle(threadId)`
 * mutation that optimistically patches the cache.
 */
export function useThreadMutes() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.chat.threadMutes(),
    queryFn: async () => {
      const res = await listThreadMutesRequest();
      const items = Array.isArray(res.data?.data?.items) ? res.data.data.items : [];
      return items.map((id) => Number(id));
    },
    staleTime: 5 * 60 * 1000,
  });

  const mutedSet = useMemo(() => new Set(query.data ?? []), [query.data]);

  const toggle = useMutation({
    mutationFn: async (threadId) => {
      const isMutedNow = mutedSet.has(Number(threadId));
      if (isMutedNow) {
        await deleteThreadMuteRequest(threadId);
        return { threadId: Number(threadId), nowMuted: false };
      }
      await storeThreadMuteRequest(threadId);
      return { threadId: Number(threadId), nowMuted: true };
    },
    onSuccess: ({ threadId, nowMuted }) => {
      queryClient.setQueryData(queryKeys.chat.threadMutes(), (old = []) => {
        const next = new Set(old.map(Number));
        if (nowMuted) next.add(threadId);
        else next.delete(threadId);
        return Array.from(next);
      });
    },
  });

  return {
    mutedSet,
    isMuted: (threadId) => mutedSet.has(Number(threadId)),
    toggle: (threadId) => toggle.mutateAsync(threadId),
    isPending: toggle.isPending,
  };
}

/**
 * Phase E / v1.75 — TanStack hook for the block list. Tracks BOTH directions:
 *
 *   - `outgoingSet`  → users the auth user has blocked
 *   - `incomingSet`  → users who have blocked the auth user
 *   - `isBlocked(u)` → true if EITHER side has acted (used by the SPA to
 *                      render the same "blocked" UI on both sides — banner,
 *                      disabled send button, hidden call button)
 *   - `isOutgoing(u)` / `isIncoming(u)` for cases that need to distinguish
 *                      (e.g. only the BLOCKER sees the Unblock button)
 *   - `toggle(u)`    → POST/DELETE the OUTGOING side only (you can only
 *                      toggle YOUR own block list, never the peer's)
 *
 * CHAT_BLOCK_CHANGED WS events should invalidate `queryKeys.chat.blocks()`
 * so the other party's UI flips immediately when they were the blockee.
 */
export function useUserBlocks() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.chat.blocks(),
    queryFn: async () => {
      const res = await listBlocksRequest();
      const data = res.data?.data ?? {};
      // Tolerate older API shape (flat `items` array) AND the v1.75 shape
      // (`outgoing` + `incoming`) so a transient backend/frontend version
      // skew degrades to outgoing-only instead of breaking.
      const outgoing = Array.isArray(data.outgoing)
        ? data.outgoing.map(Number)
        : (Array.isArray(data.items) ? data.items.map(Number) : []);
      const incoming = Array.isArray(data.incoming)
        ? data.incoming.map(Number)
        : [];
      return { outgoing, incoming };
    },
    staleTime: 5 * 60 * 1000,
  });

  const outgoingSet = useMemo(
    () => new Set(query.data?.outgoing ?? []),
    [query.data],
  );
  const incomingSet = useMemo(
    () => new Set(query.data?.incoming ?? []),
    [query.data],
  );

  const toggle = useMutation({
    mutationFn: async (userId) => {
      const isOutgoingNow = outgoingSet.has(Number(userId));
      if (isOutgoingNow) {
        await deleteBlockRequest(userId);
        return { userId: Number(userId), nowBlocked: false };
      }
      await storeBlockRequest(userId);
      return { userId: Number(userId), nowBlocked: true };
    },
    onSuccess: ({ userId, nowBlocked }) => {
      queryClient.setQueryData(queryKeys.chat.blocks(), (old) => {
        const out = new Set(old?.outgoing ?? []);
        if (nowBlocked) out.add(userId);
        else out.delete(userId);
        return { outgoing: Array.from(out), incoming: old?.incoming ?? [] };
      });
    },
  });

  return {
    outgoingSet,
    incomingSet,
    // Back-compat alias — old call sites still reading `blockedSet` keep
    // working but now reflect the bidirectional "either-side-acted" view.
    blockedSet: useMemo(() => {
      const merged = new Set(outgoingSet);
      incomingSet.forEach((id) => merged.add(id));
      return merged;
    }, [outgoingSet, incomingSet]),
    isBlocked:  (userId) => outgoingSet.has(Number(userId)) || incomingSet.has(Number(userId)),
    isOutgoing: (userId) => outgoingSet.has(Number(userId)),
    isIncoming: (userId) => incomingSet.has(Number(userId)),
    toggle: (userId) => toggle.mutateAsync(userId),
    isPending: toggle.isPending,
  };
}

/**
 * Phase B — TanStack hook for a single thread's View side-panel summary
 * (peer info, latest media, files, links, mute flag).
 */
export function useThreadView(threadId) {
  return useQuery({
    queryKey: queryKeys.chat.threadView(threadId ?? 0),
    queryFn: async () => {
      const res = await getThreadViewRequest(threadId);
      return res.data?.data ?? {};
    },
    enabled: Boolean(threadId),
    staleTime: 30 * 1000,
  });
}

/**
 * Phase C — pinned messages query + pin/unpin mutations for a thread.
 * CHAT_MESSAGE_PINNED / CHAT_MESSAGE_UNPINNED WS events should invalidate
 * this key so every viewer sees the change immediately.
 */
export function usePinnedMessages(threadId) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.chat.pinned(threadId ?? 0),
    queryFn: async () => {
      const res = await listPinnedRequest(threadId);
      return Array.isArray(res.data?.data?.items) ? res.data.data.items : [];
    },
    enabled: Boolean(threadId),
    staleTime: 30 * 1000,
  });

  const pin = useMutation({
    mutationFn: ({ threadId: tId, messageId }) => pinMessageRequest(tId, messageId),
    onSuccess: () => {
      if (threadId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.chat.pinned(threadId) });
      }
    },
  });

  const unpin = useMutation({
    mutationFn: ({ threadId: tId, messageId }) => unpinMessageRequest(tId, messageId),
    onSuccess: () => {
      if (threadId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.chat.pinned(threadId) });
      }
    },
  });

  return {
    pinned: query.data ?? [],
    isLoading: query.isLoading,
    pin:   (threadIdArg, messageId) => pin.mutateAsync({ threadId: threadIdArg, messageId }),
    unpin: (threadIdArg, messageId) => unpin.mutateAsync({ threadId: threadIdArg, messageId }),
  };
}
